import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Button,
} from "@mui/material";
import {
  AutoAwesome,
  CheckCircle,
  SmartToy,
  ChatBubbleOutline,
  ExpandMore,
  Add,
  AttachFile,
  SaveAlt,
} from "@mui/icons-material";
import { useState, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useGetTeacherSubjectsQuery, useCreateQuestionMutation } from "../../../../../../app/questionApiSlice";
import { useGetTopicsBySubjectAndLevelQuery } from "../../../../../../app/quizApiSlice";
import { FilledButton } from "../../../../../../customComponents/styled/styledButtons";
import QuestionCard from "./QuestionCard";
import { getApiBaseUrl } from "../../../../../../helpers/apiUrl";

const QUESTION_TYPES = [
  { value: "multiple choice", label: "Multiple Choice" },
  { value: "essay", label: "Essay" },
  { value: "fill-in-the-blank", label: "Fill-in-the-blank" },
  { value: "mixed", label: "Mixed" },
];

let _uid = 0;
const uid = () => `q_${++_uid}_${Date.now()}`;

const AIQuestion = () => {
  const authData = useSelector((state) => state.auth);
  const accessToken = authData?.access_token;
  const baseUrl = getApiBaseUrl();

  const { data: teacherSubjects = [], isLoading: isLoadingSubjects } =
    useGetTeacherSubjectsQuery();
  const [createQuestion] = useCreateQuestionMutation();

  // Unique subjects the teacher teaches
  const availableSubjects = useMemo(() => {
    const subjectMap = new Map();
    teacherSubjects.forEach((s) => {
      if (!subjectMap.has(s.name)) subjectMap.set(s.name, s);
    });
    return Array.from(subjectMap.values());
  }, [teacherSubjects]);

  // Form state
  const [subject, setSubject] = useState("");
  const [level, setLevel] = useState("");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [customTopicInput, setCustomTopicInput] = useState("");
  const [questionType, setQuestionType] = useState("multiple choice");
  const [count, setCount] = useState(10);
  const [extraContext, setExtraContext] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const fileInputRef = useRef(null);

  // AI streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState("");

  // Generated questions: { _uid, data: {...questionObj}, saved: bool, saving: bool }
  const [questions, setQuestions] = useState([]);
  const [saveAllLoading, setSaveAllLoading] = useState(false);

  // Levels for selected subject
  const availableLevels = useMemo(() => {
    if (!subject) return [];
    const levelSet = new Set();
    teacherSubjects
      .filter((s) => s.name === subject)
      .forEach((s) => levelSet.add(s.level));
    return Array.from(levelSet);
  }, [subject, teacherSubjects]);

  // Available topics from API
  const { data: topicsData = {}, isFetching: isFetchingTopics } =
    useGetTopicsBySubjectAndLevelQuery(
      { subject, level },
      { skip: !subject || !level }
    );
  const availableTopics = topicsData.topics || [];

  const handleSubjectChange = (e) => {
    setSubject(e.target.value);
    setLevel("");
    setSelectedTopics([]);
  };

  const handleLevelChange = (e) => {
    setLevel(e.target.value);
    setSelectedTopics([]);
  };

  const handleTopicToggle = (topicName) => {
    setSelectedTopics((prev) =>
      prev.includes(topicName)
        ? prev.filter((t) => t !== topicName)
        : [...prev, topicName]
    );
  };

  const handleAddCustomTopic = () => {
    const trimmed = customTopicInput.trim();
    if (trimmed && !selectedTopics.includes(trimmed)) {
      setSelectedTopics((prev) => [...prev, trimmed]);
    }
    setCustomTopicInput("");
  };

  const buildPrompt = useCallback(() => {
    // Topics are sent separately as a structured field — do NOT include them here
    // to avoid the model drifting to self-inferred topics.
    const lines = [
      `Generate ${count} ${questionType} questions.`,
    ];
    if (extraContext) lines.push(``, `Additional context: ${extraContext}`);
    return lines.join("\n");
  }, [count, questionType, selectedTopics, subject, level, extraContext]);

  const addQuestion = useCallback((q) => {
    setQuestions((prev) => [
      ...prev,
      { _uid: uid(), data: q, saved: false, saving: false },
    ]);
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!subject || !level) {
      setError("Please select a subject and level.");
      return;
    }
    setError("");
    setQuestions([]);
    setIsStreaming(true);

    // Auto-confirm any topic the teacher typed but didn't press Add/Enter for
    const trimmed = customTopicInput.trim();
    const effectiveTopics =
      trimmed && !selectedTopics.includes(trimmed)
        ? [...selectedTopics, trimmed]
        : selectedTopics;
    if (trimmed) {
      setSelectedTopics(effectiveTopics);
      setCustomTopicInput("");
    }

    const prompt = buildPrompt();

    try {
      const formData = new FormData();
      formData.append("prompt", prompt);
      formData.append("subject", subject);
      formData.append("level", level);
      formData.append("topics", JSON.stringify(effectiveTopics));
      if (pdfFile) {
        formData.append("pdf", pdfFile);
        setPdfFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }

      const response = await fetch(`${baseUrl}/question/ai/generate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(
          errData.message || `Request failed (${response.status})`
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let lineBuffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split("\n");
        lineBuffer = lines[lines.length - 1];

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (!line.startsWith("data: ")) continue;
          const dataStr = line.substring(6).trim();
          if (!dataStr) continue;
          try {
            const parsed = JSON.parse(dataStr);
            if (Array.isArray(parsed)) {
              parsed.forEach((q) => addQuestion(q));
            } else if (parsed && typeof parsed === "object" && parsed.body) {
              addQuestion(parsed);
            }
          } catch {
            // skip unparseable lines
          }
        }
      }
    } catch (err) {
      setError(err.message || "Failed to generate questions");
    } finally {
      setIsStreaming(false);
    }
  }, [subject, level, buildPrompt, pdfFile, baseUrl, accessToken, addQuestion]);

  const handleEditQuestion = useCallback((questionUid, updatedData) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q._uid === questionUid ? { ...q, data: updatedData } : q
      )
    );
  }, []);

  const handleDeleteQuestion = useCallback((questionUid) => {
    setQuestions((prev) => prev.filter((q) => q._uid !== questionUid));
  }, []);

  const handleSaveQuestion = useCallback(
    async (questionUid, questionData) => {
      setQuestions((prev) =>
        prev.map((q) => (q._uid === questionUid ? { ...q, saving: true } : q))
      );
      try {
        await createQuestion({
          body: questionData.body,
          topic: questionData.topic,
          subject: questionData.subject || subject,
          level: questionData.level || level,
          explanation: questionData.explanation,
          options: questionData.options,
          bank: "private",
        }).unwrap();
        setQuestions((prev) =>
          prev.map((q) =>
            q._uid === questionUid ? { ...q, saved: true, saving: false } : q
          )
        );
      } catch {
        setQuestions((prev) =>
          prev.map((q) =>
            q._uid === questionUid ? { ...q, saving: false } : q
          )
        );
        setError("Failed to save question. Please try again.");
      }
    },
    [createQuestion, subject, level]
  );

  const handleSaveAll = useCallback(async () => {
    const unsaved = questions.filter((q) => !q.saved && !q.saving);
    if (!unsaved.length) return;
    setSaveAllLoading(true);
    // Save sequentially to avoid rate issues
    for (const q of unsaved) {
      await handleSaveQuestion(q._uid, q.data);
    }
    setSaveAllLoading(false);
  }, [questions, handleSaveQuestion]);

  const canSubmit = !!subject && !!level;
  const unsavedCount = questions.filter((q) => !q.saved).length;
  const allSaved = questions.length > 0 && questions.every((q) => q.saved);

  if (isLoadingSubjects) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, fontWeight: 600, mb: 3 }}
      >
        Generate Questions with AI
      </Typography>

      <Grid container spacing={3} alignItems="flex-start">
        {/* ── Main Form ── */}
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
              borderRadius: "12px",
              border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* Subject + Level */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Subject *</InputLabel>
                  <Select
                    value={subject}
                    onChange={handleSubjectChange}
                    label="Subject *"
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">
                      <em>Select subject</em>
                    </MenuItem>
                    {availableSubjects.map((s) => (
                      <MenuItem key={s.id} value={s.name}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!subject}>
                  <InputLabel>Level *</InputLabel>
                  <Select
                    value={level}
                    onChange={handleLevelChange}
                    label="Level *"
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">
                      <em>Select level</em>
                    </MenuItem>
                    {availableLevels.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Question Type + Count */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Question Type</InputLabel>
                  <Select
                    value={questionType}
                    onChange={(e) => setQuestionType(e.target.value)}
                    label="Question Type"
                    sx={{ borderRadius: "10px" }}
                  >
                    {QUESTION_TYPES.map((qt) => (
                      <MenuItem key={qt.value} value={qt.value}>
                        {qt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Questions"
                  type="number"
                  value={count}
                  onChange={(e) =>
                    setCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))
                  }
                  inputProps={{ min: 1, max: 50 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Grid>
            </Grid>

            {/* Topics */}
            {subject && level && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, mb: 1, color: "#555" }}
                >
                  Topics{" "}
                  {isFetchingTopics && (
                    <CircularProgress size={12} sx={{ ml: 0.5 }} />
                  )}
                </Typography>
                {availableTopics.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}
                  >
                    {availableTopics.map((t) => (
                      <Chip
                        key={t.name}
                        label={t.name}
                        size="small"
                        onClick={() => handleTopicToggle(t.name)}
                        color={
                          selectedTopics.includes(t.name) ? "primary" : "default"
                        }
                        variant={
                          selectedTopics.includes(t.name) ? "filled" : "outlined"
                        }
                        sx={{ cursor: "pointer" }}
                      />
                    ))}
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Type a topic and press Enter or Add..."
                    fullWidth
                    value={customTopicInput}
                    onChange={(e) => setCustomTopicInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCustomTopic();
                      }
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                  <Tooltip title="Add topic">
                    <span>
                      <IconButton
                        onClick={handleAddCustomTopic}
                        disabled={!customTopicInput.trim()}
                        color="primary"
                        sx={{
                          border: "1px solid",
                          borderColor: "primary.main",
                          borderRadius: "8px",
                          "&.Mui-disabled": { borderColor: "#E0E0E0" },
                        }}
                      >
                        <Add />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                {selectedTopics.filter(
                  (t) => !availableTopics.find((a) => a.name === t)
                ).length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}
                  >
                    {selectedTopics
                      .filter((t) => !availableTopics.find((a) => a.name === t))
                      .map((t) => (
                        <Chip
                          key={t}
                          label={t}
                          size="small"
                          color="secondary"
                          onDelete={() =>
                            setSelectedTopics((prev) =>
                              prev.filter((x) => x !== t)
                            )
                          }
                        />
                      ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Extra context + PDF — collapsible */}
            <Accordion
              elevation={0}
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "10px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ChatBubbleOutline sx={{ fontSize: 18, color: "#666" }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Add more context (optional)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
              >
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Describe any specific requirements, difficulty level, curriculum style, or any other details..."
                  value={extraContext}
                  onChange={(e) => setExtraContext(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
                <input
                  type="file"
                  accept=".pdf"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file && file.type === "application/pdf") {
                      setPdfFile(file);
                    } else if (file) {
                      setError("Please upload a PDF file");
                    }
                  }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FilledButton
                    className="outlined"
                    size="small"
                    startIcon={<AttachFile sx={{ fontSize: 18 }} />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ height: 36, fontSize: "0.8rem" }}
                  >
                    {pdfFile ? "Change PDF" : "Attach PDF"}
                  </FilledButton>
                  {pdfFile && (
                    <Chip
                      label={pdfFile.name}
                      size="small"
                      onDelete={() => {
                        setPdfFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      sx={{ maxWidth: 220 }}
                    />
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            <FilledButton
              fullWidth
              onClick={handleGenerate}
              disabled={isStreaming || !canSubmit}
              startIcon={
                isStreaming ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AutoAwesome />
                )
              }
              sx={{ height: 48, fontSize: "1rem", fontWeight: 600 }}
            >
              {isStreaming ? "Generating Questions..." : "Generate Questions with AI"}
            </FilledButton>
          </Box>
        </Grid>

        {/* ── Generated Questions Panel ── */}
        <Grid item xs={12} md={7}>
          {/* Empty state */}
          {questions.length === 0 && !isStreaming && (
            <Box
              sx={{
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
                borderRadius: "12px",
                border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "#95969D",
                minHeight: 200,
              }}
            >
              <SmartToy sx={{ fontSize: 48, opacity: 0.4 }} />
              <Typography variant="body2" sx={{ textAlign: "center" }}>
                Generated questions will appear here
              </Typography>
              <Typography variant="caption" sx={{ textAlign: "center", maxWidth: 320 }}>
                You can edit, delete, or save each question individually, or save all at once.
              </Typography>
            </Box>
          )}

          {/* Streaming indicator while no questions yet */}
          {isStreaming && questions.length === 0 && (
            <Box
              sx={{
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
                borderRadius: "12px",
                border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
                p: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                minHeight: 200,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ fontWeight: 500, color: "primary.safeOnLight" }}>
                Generating questions...
              </Typography>
            </Box>
          )}

          {/* Question cards */}
          {questions.length > 0 && (
            <Box>
              {/* Header bar */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                  px: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {questions.length} Question{questions.length !== 1 ? "s" : ""} Generated
                  </Typography>
                  {isStreaming && <CircularProgress size={14} />}
                </Box>
                {allSaved ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <CheckCircle sx={{ color: "#4CAF50", fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: "#4CAF50", fontWeight: 600 }}>
                      All saved
                    </Typography>
                  </Box>
                ) : (
                  !isStreaming && unsavedCount > 0 && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleSaveAll}
                      disabled={saveAllLoading}
                      startIcon={
                        saveAllLoading ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <SaveAlt sx={{ fontSize: 16 }} />
                        )
                      }
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.82rem",
                      }}
                    >
                      {saveAllLoading
                        ? "Saving..."
                        : `Save All (${unsavedCount})`}
                    </Button>
                  )
                )}
              </Box>

              {/* Cards list */}
              <Box>
                {questions.map((q, idx) => (
                  <QuestionCard
                    key={q._uid}
                    index={idx}
                    question={q.data}
                    isSaved={q.saved}
                    isSaving={q.saving}
                    onEdit={(updated) => handleEditQuestion(q._uid, updated)}
                    onDelete={() => handleDeleteQuestion(q._uid)}
                    onSave={(updated) => handleSaveQuestion(q._uid, updated)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AIQuestion;
